import { type ReactNode, useEffect } from 'react';

import { Navigate } from 'react-router';
import { toast } from 'react-toastify';

import { useActiveUser } from '@ll-platform/frontend/core/auth/hooks/useActiveUser';
import { isUserInternal } from '@ll-platform/frontend/core/auth/utils/isInternal';
import { useOptionalActiveOrganization } from '@ll-platform/frontend/core/organization/ActiveOrganizationProvider';
import { Pages } from '@ll-platform/frontend/core/router/pages';

type OrganizationGuardProps = {
  children: ReactNode;
  // Some pages are used by both internal and external
  ignoreExternalUsers?: boolean;
};

export const OrganizationGuard = ({
  children,
  ignoreExternalUsers = false,
}: OrganizationGuardProps) => {
  const { activeUser } = useActiveUser();
  const { activeOrganization } = useOptionalActiveOrganization();

  useEffect(() => {
    if (
      activeOrganization ||
      (ignoreExternalUsers && !isUserInternal(activeUser))
    ) {
      return;
    }

    toast.error(
      "We are sorry, but you're not assigned to any organization yet. Please contact support.",
    );
  }, [activeUser, activeOrganization, ignoreExternalUsers]);

  if (ignoreExternalUsers && !isUserInternal(activeUser)) {
    return children;
  }

  if (!activeOrganization) {
    return <Navigate to={Pages.SettingsMyAccount} replace />;
  }

  return children;
};
